<template>
  <div id="PrivacyPolicy" class="container-fluid px-0">
    <div class="main">
      <div class="page-header">
        <div class="page-title d-flex flex-column align-items-center">
          <img class="logo" src="/img/spectrum_life_logo_light.png" />
          <h2 class="brand-text mb-0">
            Privacy Policy
          </h2>
        </div>
      </div>
      <div class="content">
        <div class="content-card">
          <h3>Virtual Primary Care – Privacy Notice</h3>
          <h3 class="pt-0">Effective Date: 25 August 2026</h3>

          <h3>1. Introduction</h3>
          <p>
            Spectrum Wellness UK Limited (trading as Spectrum.Life) is committed to protecting your personal data and
            respecting your privacy. This Privacy Notice explains how we collect, use, store, and protect your personal
            data in connection with our Virtual Primary Care (VPC) service.
          </p>
          <p>
            This Notice applies to individuals accessing VPC through an arrangement between Spectrum.Life and their
            employer, insurer, or organisation. It should be read alongside the Terms of Use for the Service.
          </p>
          <p>
            We have written this Notice in plain language in accordance with our obligations under the UK General Data
            Protection Regulation (UK GDPR) and the Data Protection Act 2018.
          </p>

          <h3>2. Who We Are</h3>
          <p>The Data Controller for the VPC service is:</p>
          <p>
            Spectrum Wellness UK Limited (trading as Spectrum.Life)<br />
            Registered in England and Wales, Company No. 11815511<br />
            Registered address: Habib House, 9 Stevenson Square, Manchester M1 1DB, UK
          </p>
          <p>
            We determine the purposes and means of processing your personal data in connection with the VPC service. In
            some cases, where we deliver services on behalf of a client organisation such as your employer or insurer,
            we may also act as a data processor under that organisation's instructions for certain administrative data.
          </p>
          <p>
            Spectrum.Life acts as independent data controller for all clinical records created through the VPC service,
            regardless of the commercial arrangement under which you access the service.
          </p>

          <h3>Data Protection Officer</h3>
          <p>
            Our Data Protection Officer can be contacted for any privacy-related query or to exercise your data
            protection rights:
          </p>
          <p>
            <b>Email: </b><a href="mailto:gdprspectrumlife@spectrum.life">gdprspectrumlife@spectrum.life</a><br />
            <b>Post: </b>Data Protection Officer, Spectrum Wellness UK Limited, Habib House, 9 Stevenson Square,
            Manchester M1 1DB, UK
          </p>

          <h3>Supervisory Authority</h3>
          <p>
            You have the right to lodge a complaint with the Information Commissioner's Office (ICO) at
            <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a>. We would welcome
            the opportunity to address any concern directly before you contact a regulator.
          </p>

          <h3>3. Services Covered by This Notice</h3>
          <p>
            This Notice applies to Spectrum.Life's Virtual Primary Care service, which provides access to remote GP
            and Advanced Nurse Practitioner (ANP) consultations, prescription fulfilment, and related services as
            described in the Terms of Use.
          </p>

          <h3>4. Personal Data We Collect</h3>
          <p>
            We collect and process the following categories of personal data:
          </p>
          <div class="grid-table" style="grid-template-columns: repeat(2, minmax(10px, 1fr));">
            <div class="grid-header">Category</div>
            <div class="grid-header">Description</div>
            <div class="grid-cell" v-for="(value, index) in collectTable" :key="'collect-' + index">{{ value }}</div>
          </div>
          <p class="mt-16">
            We collect only the personal data necessary to deliver the VPC service. Health and medical data is
            collected solely for the purpose of providing your care.
          </p>

          <h3>5. How We Collect Your Data</h3>
          <p>We collect your personal data in the following ways:</p>
          <ul>
            <li>
              <b>Directly from you</b>, when you register for the service, complete identity verification, book or
              attend a consultation, or contact our support team.
            </li>
            <li>
              <b>From your sponsoring organisation</b> (your employer, insurer, or organisation), who may share your
              identity and contact details to confirm your eligibility to access the service.
            </li>
            <li>
              <b>Automatically through the Spectrum.Life platform</b>, which collects technical and usage data as you
              interact with the service.
            </li>
          </ul>

          <h3>6. How We Use Your Data</h3>
          <p>We use your personal data for the following purposes:</p>
          <ul>
            <li>To verify your identity and eligibility for the service.</li>
            <li>To deliver GP and ANP consultations, issue prescriptions, and provide clinical care.</li>
            <li>To maintain your clinical records and ensure continuity of care.</li>
            <li>
              To facilitate electronic prescription fulfilment via our prescription partner (Signature RX).
            </li>
            <li>To send appointment confirmations, reminders, and service-related communications.</li>
            <li>
              To provide anonymised and aggregated usage reports to your sponsoring organisation, as described in
              Section 8.
            </li>
            <li>To maintain the security of the platform and prevent fraud or unauthorised access.</li>
            <li>
              To comply with our legal and regulatory obligations, including clinical governance and safeguarding
              duties.
            </li>
          </ul>
          <p>
            We do not use your personal data for marketing or advertising purposes. We do not sell your personal data
            to any third party.
          </p>

          <h3>7. Lawful Basis for Processing</h3>
          <p>We rely on the following lawful bases under UK GDPR:</p>
          <div class="grid-table" style="grid-template-columns: repeat(2, minmax(10px, 1fr));">
            <div class="grid-header">Lawful Basis</div>
            <div class="grid-header">Application</div>
            <div class="grid-cell" v-for="(value, index) in lawfulBasisTable" :key="'lawful-' + index">{{ value }}</div>
          </div>

          <h3>8. Who We Share Your Data With</h3>
          <p>
            We do not sell your personal data. We share data only with the parties listed below, in order to deliver
            the VPC service or to comply with our legal obligations.
          </p>

          <h3>Clinicians</h3>
          <p>
            Your data is shared with qualified GPs and ANPs delivering your consultation, whether employed directly by
            Spectrum.Life or engaged as clinical partners. All clinicians are bound by professional confidentiality
            obligations and by contractual confidentiality requirements with Spectrum.Life.
          </p>

          <h3>Signature RX – Prescription Partner</h3>
          <p>
            Where a prescription is issued during your consultation, the minimum data necessary to process and fulfil
            the prescription (your identity details and prescription information) is shared with Signature RX, our
            electronic prescription partner. Signature RX acts as a data processor under contract with Spectrum.Life
            and may only use your data for prescription fulfilment.
          </p>

          <h3>Salesforce – Clinical Records Platform</h3>
          <p>
            Your clinical records are stored and managed on Salesforce, which acts as a data processor on behalf of
            Spectrum.Life. Salesforce stores your data within the EEA and operates under a Data Processing Agreement
            with Spectrum.Life.
          </p>

          <h3>Amazon Web Services (AWS) – Infrastructure</h3>
          <p>
            The Spectrum.Life platform is hosted on AWS infrastructure. AWS acts as a data processor and stores data
            within EEA regions. All data is encrypted at rest and in transit.
          </p>

          <h3>Telecom Stack – Telephony Provider</h3>
          <p>
            Spectrum.Life uses Telecom Stack as its telephony infrastructure provider for the VPC service. Call routing
            for GP and ANP consultations is handled via the Telecom Stack platform. Where you have consented to your
            consultation being recorded, the audio recording is stored within the Telecom Stack environment. Telecom
            Stack acts as a data processor under contract with Spectrum.Life and processes data only as directed by
            Spectrum.Life. Recordings are retained for 12 months and then securely deleted.
          </p>

          <h3>Your Sponsoring Organisation</h3>
          <p>
            Your employer, insurer, or organisation does not receive your individual clinical information. They may
            receive periodic anonymised and aggregated usage reports (for example, total consultation volumes and
            broad service categories). These reports cannot identify you individually. This sharing is carried out on
            the basis of our legitimate interests under Art. 6(1)(f) UK GDPR, as described in Section 7.
          </p>
          <p>
            Where your insurer requires minimal administrative data for billing purposes (such as a membership
            reference and date of service), we may share that limited information. We do not share clinical notes or
            consultation content with insurers.
          </p>

          <h3>Legal and Regulatory Disclosures</h3>
          <p>
            We may disclose your personal data to regulatory bodies, law enforcement, or courts where we are required
            to do so by law. We may also share data where necessary to protect your vital interests or the vital
            interests of others, including for safeguarding purposes as described in the Terms of Use.
          </p>

          <h3>9. International Data Transfers</h3>
          <p>
            Spectrum.Life stores and processes personal data within the UK and the EEA. Our primary cloud
            infrastructure (AWS, Salesforce) is hosted within the EEA. We do not routinely transfer your personal
            data outside the UK or EEA.
          </p>
          <p>
            Where any transfer outside the UK or EEA becomes necessary, we will ensure that appropriate safeguards
            are in place, such as an International Data Transfer Agreement (IDTA) or UK Addendum to the EU Standard
            Contractual Clauses. Any such transfers will be assessed for adequacy and documented.
          </p>

          <h3>10. Data Retention</h3>
          <p>
            We retain your personal data only for as long as necessary for the purposes described in this Notice, and
            in accordance with our legal and clinical governance obligations. Retention periods for the VPC service
            are:
          </p>
          <div class="grid-table" style="grid-template-columns: repeat(2, minmax(10px, 1fr));">
            <div class="grid-header">Data Category</div>
            <div class="grid-header">Retention Period</div>
            <div class="grid-cell" v-for="(value, index) in retentionTable" :key="'retention-' + index">{{ value }}</div>
          </div>
          <p class="mt-16">
            When data is no longer required, we securely delete or anonymise it in accordance with our data retention
            policy.
          </p>

          <h3>11. Data Security</h3>
          <p>
            Spectrum.Life has implemented appropriate technical and organisational measures to protect your personal
            data from unauthorised access, disclosure, alteration, or destruction. These include:
          </p>
          <ul>
            <li>Encryption of all personal data in transit (TLS) and at rest (AES-256).</li>
            <li>
              Role-based access controls limiting access to personal data to authorised staff and clinicians on a
              need-to-know basis.
            </li>
            <li>Multi-factor authentication for staff with access to clinical systems.</li>
            <li>Audit logging of access to clinical records.</li>
            <li>Security monitoring, vulnerability management, and regular penetration testing.</li>
            <li>Staff training on data protection and information security.</li>
          </ul>
          <p>
            Our information security management aligns with ISO 27001:2022 and Cyber Essentials standards.
          </p>

          <h3>12. Your Data Subject Rights</h3>
          <p>Under UK GDPR, you have the following rights in relation to your personal data:</p>
          <div class="grid-table" style="grid-template-columns: repeat(2, minmax(10px, 1fr));">
            <div class="grid-header">Right</div>
            <div class="grid-header">Description</div>
            <div class="grid-cell" v-for="(value, index) in rightsTable" :key="'rights-' + index">{{ value }}</div>
          </div>
          <p class="mt-16">
            To exercise any of the above rights, please contact our Data Protection Officer at
            <a href="mailto:gdprspectrumlife@spectrum.life">gdprspectrumlife@spectrum.life</a>. We will respond
            within one month of receiving a verified request, or notify you if an extension of up to a further two
            months is required.
          </p>

          <h3>13. Automated Decision-Making</h3>
          <p>
            Spectrum.Life does not make decisions that produce legal effects or similarly significant effects on you
            based solely on automated processing. All clinical decisions are made by qualified human clinicians. No
            automated system is used to deny access to the service or to make clinical determinations without human
            involvement.
          </p>

          <h3>14. Children and Young People</h3>
          <p>
            For users aged 0–15, a parent or legal guardian must consent to and be present during consultations, as
            set out in the Terms of Use. Personal data relating to minors is processed under Article 6(1)(a) (consent
            of parent or guardian) and Article 9(2)(h) (provision of healthcare) UK GDPR.
          </p>
          <p>
            Records relating to minors are retained until the individual reaches the age of 25, or longer if required
            by applicable law or safeguarding obligations.
          </p>

          <h3>15. Cookies and Tracking Technologies</h3>
          <p>
            The Spectrum.Life platform uses cookies and similar technologies to support its operation. Strictly
            necessary cookies are used to maintain your authenticated session and to keep the platform secure.
            Analytics cookies, where used, collect aggregated and anonymised data to help us improve the service.
          </p>
          <p>
            Non-essential cookies are only set with your consent. You can manage your cookie preferences through your
            browser settings. Disabling strictly necessary cookies may prevent certain features of the platform from
            functioning correctly.
          </p>

          <h3>16. Updates to This Notice</h3>
          <p>
            We may update this Privacy Notice from time to time to reflect changes in our services, our processing
            activities, or applicable law. We will publish the updated Notice with a revised effective date. Where
            changes are material, we will notify you directly where reasonably practicable.
          </p>
          <p>
            If you have any questions about this Notice, please contact our Data Protection Officer at
            <a href="mailto:gdprspectrumlife@spectrum.life">gdprspectrumlife@spectrum.life</a>.
          </p>
          <p>
            Spectrum Wellness UK Limited (trading as Spectrum.Life) | Registered in England and Wales No. 11815511<br />
            Habib House, 9 Stevenson Square, Manchester M1 1DB, UK |
            <a href="mailto:gdprspectrumlife@spectrum.life">gdprspectrumlife@spectrum.life</a> |
            <a href="https://spectrum.life" target="_blank" rel="noopener noreferrer">spectrum.life</a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PrivacyPolicyVpc",
  data() {
    return {
      collectTable: [
        "Identity and contact data",
        "Name, date of birth, gender, postal address, email address, telephone number, employee or membership reference from your employer or insurer.",
        "Identity verification data",
        "Copy of photographic identification (passport, driving licence, biometric residence permit, or EEA national identity card) uploaded at registration, as required by the Terms of Use.",
        "Health and medical data (special category)",
        "Medical history, symptoms, presenting issues, clinical notes from consultations, diagnoses, prescriptions, and referral information. This is special category data under UK GDPR and is subject to enhanced protection.",
        "Appointment and consultation data",
        "Booking records, consultation logs, appointment confirmations, and cancellation records.",
        "Technical and usage data",
        "IP address, device type, browser type, login times, and platform usage logs collected automatically when you use the Spectrum.Life app or web portal.",
        "Communication data",
        "Records of messages or correspondence sent to our support team, and appointment reminders and notifications sent to you."
      ],
      lawfulBasisTable: [
        "Performance of a contract (Art. 6(1)(b))",
        "Processing necessary to deliver the VPC service to you under the Terms of Use.",
        "Legal obligation (Art. 6(1)(c))",
        "Processing required to comply with legal or regulatory obligations, including clinical record-keeping, safeguarding duties, and tax obligations.",
        "Legitimate interests (Art. 6(1)(f))",
        "Processing for platform security, fraud prevention, and providing anonymised usage reports to your sponsoring organisation, where our interests are balanced against your rights.",
        "Healthcare provision – special category data (Art. 9(2)(h))",
        "Processing of health data necessary for the provision of healthcare by or under the responsibility of clinicians bound by professional confidentiality obligations. This is the primary basis for processing your clinical data.",
        "Parental or guardian consent – minors (Art. 6(1)(a) / Art. 9(2)(a))",
        "Where services are accessed by or on behalf of a child, processing is based on the consent of a parent or legal guardian, in addition to Art. 9(2)(h) for clinical data."
      ],
      retentionTable: [
        "GP medical records (special category health data)",
        "Minimum 10 years from the date of the last entry in the record.",
        "Mental health clinical records",
        "Minimum 10 years from the date of the last consultation.",
        "Records relating to minors",
        "Until the individual reaches the age of 25, or longer if required by applicable law or safeguarding obligations.",
        "General personal data (name, contact details)",
        "Retained during active use of the service, then up to 7 years following account deactivation, in accordance with our data retention policy.",
        "Call recording data",
        "Retained for 12 months from the date of the recording on the PBX, then securely deleted. Where a clinician saves a recording extract to the clinical record, that extract is retained as part of the clinical record for a minimum of 10 years in accordance with the clinical retention period above.",
        "Technical and usage data",
        "Short-term retention for operational and security purposes; anonymised or deleted within a reasonable period."
      ],
      rightsTable: [
        "Right to be informed",
        "To be told how your personal data is collected and used. This Notice fulfils that obligation.",
        "Right of access",
        "To request a copy of the personal data we hold about you (a Subject Access Request).",
        "Right to rectification",
        "To request correction of inaccurate or incomplete data.",
        "Right to erasure",
        "To request deletion of your personal data in certain circumstances. Note that clinical records may be retained for the periods set out in Section 10 where required by law or clinical governance obligations.",
        "Right to restrict processing",
        "To request that we pause processing of your data in certain circumstances.",
        "Right to data portability",
        "To request a copy of data you have provided to us in a machine-readable format, where the lawful basis is contract or consent and processing is by automated means.",
        "Right to object",
        "To object to processing carried out on the basis of legitimate interests. You have an absolute right to object to processing for direct marketing (we do not carry out such processing).",
        "Right to withdraw consent",
        "Where processing is based on consent, to withdraw that consent at any time without affecting the lawfulness of prior processing.",
        "Right to lodge a complaint",
        "To contact the ICO at ico.org.uk if you believe we have not handled your data lawfully."
      ]
    };
  }
};
</script>

<style lang="less">
#PrivacyPolicy {
  .main {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  .page-header {
    padding: 60px 20px 20px;
    background-color: #fbfafa;
  }

  .logo {
    max-width: 350px;
    width: 100%;
    padding-bottom: 50px;
  }

  .page-title {
    h2 {
      text-align: center;
      font-size: 3.8rem;
      letter-spacing: normal;
      font-family: "PlusJakartaSansBold";
    }
  }

  .grid-table {
    min-width: 440px;
    font-size: 1.6rem;
    display: grid;
    gap: 4px;
    width: 100%;
    margin: auto;
    border: 1px solid #ddd;
    padding: 4px;
  }

  .grid-header {
    font-family: "PlusJakartaSansBold";
    font-weight: bold;
    border: 1px solid #ddd;
    background: #f4f4f4;
    padding: 10px;
    text-align: left;
  }

  .grid-cell {
    padding: 10px;
    text-align: left;
    border: 1px solid #ddd;
  }

  .content {
    flex: 1 1 100%;
    padding: 20px 20px 60px;
    background-color: #fbfafa;
  }

  .content-card {
    height: 100%;
    background: #ffffff;
    max-width: 1000px;
    padding: 40px 60px 30px;
    margin: 0 auto;
    border-radius: 16px;
    overflow-x: scroll;

    .mt-16 {
      margin-top: 16px;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      color: #212529;
      font-family: "PlusJakartaSansBold";
    }

    h3 {
      font-size: 2.2rem;
      letter-spacing: 0;
      padding: 32px 0 0;
      margin: 0 0 24px;
    }

    h4 {
      font-size: 1.6rem;
      letter-spacing: 0;
      padding: 0;
      margin: 0 0 4px;
    }

    p {
      color: #212529;
      padding: 0;
      margin: 0 0 16px;
      font-size: 1.6rem;
    }

    ul,
    ol {
      color: #212529;
      margin: 0 0 16px;
    }

    li {
      color: #212529;
      font-size: 1.6rem;
      margin-bottom: 8px;
    }

    b {
      font-family: "PlusJakartaSansBold";
    }

    a {
      color: #3c16f9;

      &:hover,
      &:active {
        color: #3c16f9;
      }
    }
  }
  @media only screen and (max-width: 768px) {
    .content-card {
      padding: 30px 20px;
    }
    .grid-table {
      font-size: 1.4rem;
    }
  }
  @media only screen and (max-width: 768px) {
    .grid-table {
      font-size: 1rem;
    }
  }
}
</style>
